import { cn } from "@lib/cn"
import type { ReactNode } from "react"

/**
 * The one pill (PRD-60 chunk 0), same vocabulary as the professor SPA's
 * Badge. Variants name a MEANING, so a reader learns the colour once and it
 * holds across every surface:
 *
 *   accent    — the primary object's own state
 *   success   — active, accepted, on track
 *   attention — needs the admin (an invitation pending, a demo data source)
 *   danger    — a real problem, never mere archival
 *   neutral   — a fact with no valence ("En papelera", a count, a role)
 *   outline   — a label on a tinted ground, where a fill would fight it
 *
 * `destructive` used to paint "Eliminado" red on five tables; a soft-deleted
 * row is archived, not dangerous, so it is `neutral` now.
 */
type Variant = "accent" | "success" | "attention" | "danger" | "neutral" | "outline"

const variants: Record<Variant, string> = {
  accent: "bg-accent-soft text-accent-ink",
  success: "bg-success-soft text-success-ink",
  attention: "bg-attention-soft text-attention-ink",
  danger: "bg-danger-soft text-danger-ink",
  neutral: "bg-inset text-ink-muted",
  outline: "border border-border text-ink-muted",
}

export function Badge({
  children,
  variant = "neutral",
  className,
}: {
  children: ReactNode
  variant?: Variant
  className?: string
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        variants[variant],
        className,
      )}
    >
      {children}
    </span>
  )
}
